/**
 * Sample service provider contracts for OEMA.
 *
 * Providers are ROLES, as in the equipment register (`fleet.ts`) — inventing
 * company names that resemble real handlers or caterers would put fabricated
 * contracts and fabricated service levels against organisations that exist.
 * Dates hang off `TODAY`, so the register always shows one contract past its
 * notice deadline, one expiring, one expired and the rest in force, whenever
 * it is opened. No financial terms, by the register's own rule.
 */
import type { Contract, ContractRegister } from "../domain/contracts";
import { TODAY } from "./clock";

function day(offset: number): string {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

const C = (c: Contract): Contract => c;

export function mockContracts(): ContractRegister {
  const by = "Commercial and contracts office";
  const at = (d: number) => new Date(TODAY.getTime() + d * 86_400_000).toISOString();
  const list: Contract[] = [
    C({
      id: "ct-gh",
      provider: "Ground handling agent",
      kind: "ground_handling",
      title: "Ramp and passenger handling services",
      reference: "OEMA-OPS-GH-01",
      scope:
        "Ramp handling, baggage handling, passenger check-in and boarding, and aircraft marshalling for scheduled and charter flights at the contact and remote stands.",
      services: [
        "Ramp handling and pushback",
        "Baggage make-up and reclaim",
        "Check-in and boarding",
        "Marshalling and wing-walking",
        "Ground support equipment on the ramp",
      ],
      exclusions: "Aircraft fuelling, catering uplift, cabin cleaning and security screening.",
      serviceLevels: [
        {
          id: "sl1",
          measure: "First bag on the reclaim belt",
          target: "within 15 minutes of on-blocks for 95% of arrivals",
          frequency: "monthly",
          measuredBy: "Airport operations, from the baggage system log",
        },
        {
          id: "sl2",
          measure: "Last bag on the reclaim belt",
          target: "within 35 minutes of on-blocks for 95% of arrivals",
          frequency: "monthly",
          measuredBy: "Airport operations, from the baggage system log",
        },
        {
          id: "sl3",
          measure: "Turnaround delays caused by handling",
          target: "fewer than 1.5% of departures",
          frequency: "monthly",
          measuredBy: "Delay codes in the movement log",
        },
        {
          id: "sl4",
          measure: "Ramp damage events",
          target: "no more than 2 in any 90 days",
          frequency: "quarterly",
          measuredBy: "Safety reports",
          onBreach: "Joint safety review within 10 working days",
        },
      ],
      start: day(-900),
      end: day(560),
      noticeDays: 180,
      renewal: "option",
      owner: "Head of Airside Operations",
      contact: { name: "Station manager", phone: "", email: "" },
      insuranceExpiry: day(120),
    }),
    C({
      id: "ct-fuel",
      provider: "Fuel services",
      kind: "fuel",
      title: "Into-plane fuelling and hydrant operation",
      reference: "OEMA-OPS-FU-03",
      scope:
        "Into-plane fuelling by hydrant dispenser and bowser, operation and daily checks of the hydrant system, and fuel quality control to the recognised joint inspection standards.",
      services: [
        "Into-plane fuelling",
        "Hydrant system operation",
        "Fuel quality control and sampling",
        "Spill first response",
      ],
      exclusions: "Maintenance of the hydrant pits and the fuel farm structure, which the airport retains.",
      serviceLevels: [
        {
          id: "sl1",
          measure: "Fuelling started on request",
          target: "within 10 minutes for 98% of requests",
          frequency: "monthly",
          measuredBy: "Fuel dispatch log",
        },
        {
          id: "sl2",
          measure: "Fuel quality test records",
          target: "every delivery sampled and recorded",
          frequency: "per_event",
          measuredBy: "Quality records, audited quarterly",
        },
      ],
      start: day(-1300),
      end: day(28),
      noticeDays: 90,
      renewal: "auto",
      owner: "Head of Airside Operations",
      insuranceExpiry: day(210),
    }),
    C({
      id: "ct-clean",
      provider: "Terminal cleaning",
      kind: "cleaning",
      title: "Terminal and landside cleaning",
      reference: "OEMA-FAC-CL-02",
      scope:
        "Cleaning of the terminal public areas, washrooms, gates and landside forecourt, around the clock, to the airport's cleaning schedule.",
      services: [
        "Terminal public areas",
        "Washrooms, inspected hourly",
        "Gate lounges between departures",
        "Landside forecourt and car parks",
      ],
      serviceLevels: [
        {
          id: "sl1",
          measure: "Washroom inspections completed",
          target: "every hour, 98% of scheduled inspections",
          frequency: "weekly",
          measuredBy: "Facilities quality audits",
        },
        {
          id: "sl2",
          measure: "Response to a reported spill",
          target: "within 10 minutes",
          frequency: "per_event",
          measuredBy: "Helpdesk tickets",
        },
      ],
      start: day(-700),
      end: day(-12),
      noticeDays: 60,
      renewal: "fixed",
      owner: "Terminal Services Manager",
      insuranceExpiry: day(-40),
    }),
    C({
      id: "ct-cat",
      provider: "Catering services",
      kind: "catering",
      title: "In-flight catering uplift",
      scope:
        "Preparation, delivery and uplift of in-flight catering, and removal of offload waste to the approved disposal route.",
      services: ["Meal preparation", "Catering uplift by high-loader", "Offload and waste removal"],
      serviceLevels: [
        {
          id: "sl1",
          measure: "Catering on board before boarding",
          target: "for 99% of departures",
          frequency: "monthly",
          measuredBy: "Airline reports and the movement log",
        },
      ],
      start: day(-400),
      end: day(70),
      noticeDays: 90,
      renewal: "auto",
      owner: "Commercial Manager",
      insuranceExpiry: day(300),
    }),
    C({
      id: "ct-agl",
      provider: "AGL maintenance contractor",
      kind: "maintenance",
      title: "Airfield ground lighting maintenance",
      reference: "OEMA-ENG-AGL-07",
      scope:
        "Preventive and corrective maintenance of the airfield ground lighting, constant current regulators and the AGL control and monitoring system.",
      services: [
        "Night inspections of the AGL",
        "Lamp and fitting replacement",
        "CCR maintenance",
        "AGL monitoring system support",
      ],
      exclusions: "Cable replacement beyond 50 metres, and new installations, which are separate works.",
      serviceLevels: [
        {
          id: "sl1",
          measure: "Response to a Category 1 AGL failure",
          target: "on site within 30 minutes, day or night",
          frequency: "per_event",
          measuredBy: "Work orders in this platform",
        },
        {
          id: "sl2",
          measure: "AGL serviceability",
          target: "at or above the regulatory minimum at every inspection",
          frequency: "daily",
          measuredBy: "Night AGL inspection",
        },
      ],
      start: day(-200),
      end: day(900),
      noticeDays: 120,
      renewal: "option",
      owner: "Head of Engineering",
    }),
  ];
  return { versions: list.map((c, i) => ({ id: `v-${c.id}`, contractId: c.id, at: at(-30 + i), by, data: c })) };
}
